import { useState, useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ReactComponent as DigntecLogo } from '../../Assets/images/DigntecLogo.svg';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { api } from '../../api/api';
import toast from 'react-hot-toast';
import './login.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function StoreRegister() {
  const { auth } = useSelector(s => s.User);
  const dispatch = useDispatch();

  const [step, setStep] = useState(1); // 1: zip+phone, 2: google login, 3: done
  const [zipcode, setZipcode] = useState('');
  const [town, setTown] = useState('');
  const [state, setState] = useState('');
  const [phone, setPhone] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storeType, setStoreType] = useState('new'); // 가입 방법 A: 신규/기존 (12.01-D011)
  const [screenCount, setScreenCount] = useState(3); // TV(스크린) 수 — 나중에 바꿀 수 있음
  const [zipLoading, setZipLoading] = useState(false);
  const [zipError, setZipError] = useState('');
  const [storeNameError, setStoreNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  // Google Sign-In SDK 로드
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  const googleHandlerRef = useRef(null);
  // Step 2에서 Google 버튼 렌더
  useEffect(() => {
    if (step !== 2 || !GOOGLE_CLIENT_ID || !window.google) return;
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      // 버튼을 그린 뒤 고른 값(매장 구분·TV 수 등)도 쓰도록 최신 핸들러를 호출
      callback: (r) => googleHandlerRef.current(r),
      auto_select: false,
      cancel_on_tap_outside: true,
    });
    // FedCM 비활성화 — 계정 선택 팝업 강제 표시
    window.google.accounts.id.disableAutoSelect();
    window.google.accounts.id.renderButton(
      document.getElementById('google-signin-btn'),
      { theme: 'outline', size: 'large', text: 'continue_with', width: 320 }
    );
  }, [step]);

  // Zip code → City/State 조회
  const lookupZip = async () => {
    const zip = zipcode.replace(/\D/g, '').slice(0, 5);
    if (zip.length !== 5) { setZipError('5자리 우편번호를 입력하세요'); return; }
    setZipLoading(true);
    setZipError('');
    try {
      const res = await api.get(`/zipcode/${zip}`);
      const city = res.data.city || '';
      const st = res.data.stateAbbr || res.data.state || '';
      setTown(city);
      setState(st);
      if (city && st) {
        const autoName = `${st}_${city.replace(/\s+/g, '')}`;
        setStoreName(autoName);
        setStoreNameError('');
        // 중복 체크
        try {
          const checkRes = await api.get(`/store/check-name/${encodeURIComponent(autoName)}`).catch(() => null);
          const isDup = checkRes?.data?.exists;
          if (isDup) {
            setStoreNameError(`"${autoName}" 이미 등록된 매장입니다. 매장명을 수정하세요 (예: ${autoName}_2)`);
            setTimeout(() => document.getElementById('store-name-input')?.focus(), 100);
          } else {
            setTimeout(() => document.getElementById('phone-input')?.focus(), 100);
          }
        } catch (_) {
          // 중복 체크 실패해도 진행 허용
          setTimeout(() => document.getElementById('phone-input')?.focus(), 100);
        }
      }
    } catch (e) {
      setZipError('우편번호를 찾을 수 없습니다');
      setTown('');
      setState('');
    }
    setZipLoading(false);
  };

  useEffect(() => {
    const zip = zipcode.replace(/\D/g, '');
    if (zip.length === 5) lookupZip();
    else { setTown(''); setState(''); setZipError(''); }
  }, [zipcode]);

  // 전화번호 포맷
  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const canProceed = town && state && phone.replace(/\D/g, '').length >= 10;

  // Google 응답 처리
  const handleGoogleResponse = async (response) => {
    if (!response.credential) return;
    setSubmitting(true);
    try {
      const res = await api.post('/user/google-register', {
        googleToken: response.credential,
        phone: phone.replace(/\D/g, ''),
        zipcode: zipcode.replace(/\D/g, ''),
        town,
        state,
        storeName,
        storeType, screenCount: storeType === 'new' ? screenCount : undefined,
      });

      if (res.data.auth) {
        // 이미 승인된 사용자 — 바로 로그인
        dispatch({ type: 'LOGIN_SUCCESS', payload: res.data.user });
        toast.success('로그인 성공!');
      } else if (res.data.pending) {
        setRegistered(true);
        setStep(3);
        toast.success('등록 완료! 관리자 승인을 기다려 주세요.');
      }
    } catch (e) {
      const msg = e.response?.data?.message || '등록 실패';
      toast.error(msg);
    }
    setSubmitting(false);
  };
  googleHandlerRef.current = handleGoogleResponse;


  if (auth) return <Navigate to="/" />;

  return (
    <div className="lp-root">
      {/* Left panel */}
      <div className="lp-left">
        <div className="lp-brand">
          <div className="lp-brand-logo">
            <DigntecLogo style={{ width: '160px', height: 'auto', filter: 'brightness(0) invert(1)' }} />
          </div>
          <h1 className="lp-brand-title">Store Owner<br />Registration</h1>
          <p className="lp-brand-sub">
            매장 점주님을 위한 간편 등록입니다.<br />
            우편번호와 전화번호를 입력하고 Google 계정으로 등록하세요.
          </p>
          <div className="lp-features">
            <div className="lp-feature-item">
              <div className="lp-feature-icon"><StorefrontIcon /></div>
              <div>
                <div className="lp-feature-title">3단계 간편 등록</div>
                <div className="lp-feature-desc">우편번호 → 전화번호 → Google 로그인</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="lp-right">
        <div className="lp-form-wrap">
          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            {[1, 2, 3].map(s => (
              <div key={s} style={{
                flex: 1, height: 4, borderRadius: 2,
                background: step >= s ? '#551FFF' : '#e5e7eb',
                transition: 'background 0.3s',
              }} />
            ))}
          </div>

          {/* Step 1: Zip + Phone */}
          {step === 1 && (
            <>
              <div className="lp-form-header">
                <h2 className="lp-form-title">매장 정보 입력</h2>
                <p className="lp-form-subtitle">우편번호로 매장 위치를 확인합니다</p>
              </div>

              <div className="lp-field">
                <label className="lp-label">Zip Code *</label>
                <input
                  className="lp-input"
                  type="text"
                  value={zipcode}
                  onChange={e => setZipcode(e.target.value)}
                  placeholder="예: 90210"
                  maxLength={5}
                  inputMode="numeric"
                />
                {zipLoading && <span style={{ fontSize: 12, color: '#888' }}>조회 중...</span>}
                {zipError && <span style={{ fontSize: 12, color: '#ef4444' }}>{zipError}</span>}
              </div>

              {town && state && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <div className="lp-field" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="lp-label">City</label>
                    <input className="lp-input" value={town} readOnly style={{ background: '#f9fafb' }} />
                  </div>
                  <div className="lp-field" style={{ flex: 0.4, marginBottom: 0 }}>
                    <label className="lp-label">State</label>
                    <input className="lp-input" value={state} readOnly style={{ background: '#f9fafb' }} />
                  </div>
                </div>
              )}

              <div className="lp-field">
                <label className="lp-label">Store Name *</label>
                <input
                  id="store-name-input"
                  className="lp-input"
                  type="text"
                  value={storeName}
                  onChange={e => { setStoreName(e.target.value); setStoreNameError(''); }}
                  placeholder="매장명 (자동 입력)"
                  style={storeNameError ? { borderColor: '#ef4444', background: '#fef2f2' } : {}}
                />
                {storeNameError && <span style={{ fontSize: 12, color: '#ef4444' }}>{storeNameError}</span>}
              </div>

              <div className="lp-field">
                <label className="lp-label">매장 구분 · Store type</label>
                <select className="lp-input" value={storeType} onChange={e => setStoreType(e.target.value)}>
                  <option value="new">신규 매장 (새로 설치) · New store</option>
                  <option value="existing">이미 Digntec 을 쓰는 매장 · Existing store</option>
                </select>
              </div>
              {storeType === 'new' && (
                <div className="lp-field">
                  <label className="lp-label">TV(스크린) 수 · Number of screens</label>
                  <select className="lp-input" value={screenCount} onChange={e => setScreenCount(Number(e.target.value))}>
                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>나중에 바꿀 수 있습니다 · You can change this later</div>
                </div>
              )}

              <div className="lp-field">
                <label className="lp-label">Phone Number *</label>
                <input
                  id="phone-input"
                  className="lp-input"
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(formatPhone(e.target.value))}
                  placeholder="(000) 000-0000"
                  inputMode="tel"
                />
                <span style={{ fontSize: 11, color: '#888' }}>점주 확인용 전화번호</span>
              </div>

              <button
                className="lp-btn-login"
                onClick={() => setStep(2)}
                disabled={!canProceed}
              >
                다음: Google 계정 연결
              </button>

              <p className="lp-support" style={{ marginTop: 16 }}>
                이미 계정이 있으신가요? <Link to="/login" style={{ color: '#551FFF', fontWeight: 600 }}>로그인</Link>
              </p>
            </>
          )}

          {/* Step 2: Google Login */}
          {step === 2 && (
            <>
              <div className="lp-form-header">
                <h2 className="lp-form-title">Google 계정 연결</h2>
                <p className="lp-form-subtitle">
                  {state} {town} · {phone}<br />
                  Google 계정으로 로그인하여 등록을 완료하세요
                </p>
              </div>

              <div style={{
                background: '#f8f7ff', borderRadius: 12, padding: 16, marginBottom: 20,
                border: '1px solid rgba(85,31,255,0.1)',
              }}>
                <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>매장 위치</div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>
                  {storeName ? `${storeName} — ` : ''}{town}, {state} {zipcode}
                </div>
                <div style={{ fontSize: 13, color: '#555', marginTop: 8, marginBottom: 2 }}>전화번호</div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{phone}</div>
              </div>

              {submitting ? (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <img src="/images/spiner.gif" alt="loading" style={{ width: 40 }} />
                  <p style={{ color: '#888', fontSize: 13, marginTop: 8 }}>등록 중...</p>
                </div>
              ) : (
                <div id="google-signin-btn" style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }} />
              )}

              {!GOOGLE_CLIENT_ID && (
                <p style={{ color: '#ef4444', fontSize: 12, textAlign: 'center' }}>
                  Google Client ID가 설정되지 않았습니다. 관리자에게 문의하세요.
                </p>
              )}

              <button
                className="lp-btn-login"
                onClick={() => setStep(1)}
                style={{ background: '#e5e7eb', color: '#333' }}
              >
                이전 단계로
              </button>
            </>
          )}

          {/* Step 3: Done */}
          {step === 3 && (
            <>
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', marginBottom: 2 }} />
                <h2 style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>등록 완료!</h2>
                <p style={{ color: '#888', fontSize: 14, lineHeight: 1.6 }}>
                  관리자가 점주 정보를 확인한 후<br />
                  승인 알림을 보내드립니다.
                </p>
                <div style={{
                  background: '#f8f7ff', borderRadius: 12, padding: 16, marginTop: 24,
                  border: '1px solid rgba(85,31,255,0.1)', textAlign: 'left',
                }}>
                  <div style={{ fontSize: 12, color: '#888' }}>등록 정보</div>
                  <div style={{ fontSize: 14, marginTop: 4 }}>
                    {storeName && <><strong>{storeName}</strong><br /></>}
                    {town}, {state} {zipcode}<br />
                    {phone}
                  </div>
                </div>
              </div>

              <Link to="/login">
                <button className="lp-btn-login" style={{ width: '100%' }}>
                  로그인 페이지로
                </button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default StoreRegister;
